/**
 * Vibe Theme Plugin
 *
 * Custom Monaco Editor theme matching Vibe Designer's dark theme.
 */
import type { editor } from 'monaco-editor';
import type { EditorPlugin } from './index';

const VIBE_THEME: editor.IStandaloneThemeData = {
  base: 'vs-dark',
  inherit: true,
  rules: [
    // Comments
    { token: 'comment', foreground: '6b7280', fontStyle: 'italic' },
    { token: 'comment.doc', foreground: '6b7280' },

    // Keywords
    { token: 'keyword', foreground: 'c084fc' },
    { token: 'keyword.control', foreground: 'c084fc' },
    { token: 'keyword.operator', foreground: 'c084fc' },

    // Strings
    { token: 'string', foreground: '86efac' },
    { token: 'string.escape', foreground: '6ee7b7' },

    // Numbers
    { token: 'number', foreground: 'fbbf24' },
    { token: 'number.float', foreground: 'fbbf24' },

    // Types
    { token: 'type', foreground: '67e8f9' },
    { token: 'type.identifier', foreground: '67e8f9' },

    // Functions
    { token: 'function', foreground: '93c5fd' },
    { token: 'function.declaration', foreground: '93c5fd' },

    // Variables
    { token: 'variable', foreground: 'e4e4e7' },
    { token: 'variable.parameter', foreground: 'fca5a5' },
    { token: 'variable.other', foreground: 'e4e4e7' },

    // Constants
    { token: 'constant', foreground: 'fbbf24' },
    { token: 'constant.language', foreground: 'c084fc' },
    { token: 'constant.numeric', foreground: 'fbbf24' },

    // Operators
    { token: 'operator', foreground: 'f472b6' },

    // Punctuation
    { token: 'delimiter', foreground: '9ca3af' },
    { token: 'delimiter.bracket', foreground: '9ca3af' },

    // Tags (JSX/HTML)
    { token: 'tag', foreground: 'f87171' },
    { token: 'tag.attribute.name', foreground: 'c084fc' },
    { token: 'tag.attribute.value', foreground: '86efac' },

    // Regex
    { token: 'regexp', foreground: 'fb923c' },

    // Markdown
    { token: 'markup.heading', foreground: '93c5fd', fontStyle: 'bold' },
    { token: 'markup.bold', fontStyle: 'bold' },
    { token: 'markup.italic', fontStyle: 'italic' },
    { token: 'markup.link', foreground: '67e8f9' },

    // JSON
    { token: 'string.key.json', foreground: 'c084fc' },
    { token: 'string.value.json', foreground: '86efac' },

    // CSS
    { token: 'attribute.name.css', foreground: '67e8f9' },
    { token: 'attribute.value.css', foreground: '86efac' },
    { token: 'selector.css', foreground: 'f87171' },
  ],
  colors: {
    // Editor colors
    'editor.background': '#0a0a0b',
    'editor.foreground': '#e4e4e7',
    'editor.lineHighlightBackground': '#1a1a1d',
    'editor.selectionBackground': '#6366f140',
    'editor.inactiveSelectionBackground': '#6366f120',
    'editor.selectionHighlightBackground': '#6366f130',
    'editor.wordHighlightBackground': '#6366f130',
    'editor.findMatchBackground': '#fbbf2440',
    'editor.findMatchHighlightBackground': '#fbbf2420',

    // Editor cursor
    'editorCursor.foreground': '#6366f1',
    'editorCursor.background': '#0a0a0b',

    // Editor whitespace
    'editorWhitespace.foreground': '#27272a',

    // Editor indent guides
    'editorIndentGuide.background': '#27272a',
    'editorIndentGuide.activeBackground': '#3f3f46',

    // Editor line numbers
    'editorLineNumber.foreground': '#52525b',
    'editorLineNumber.activeForeground': '#a1a1aa',

    // Editor gutter
    'editorGutter.background': '#0a0a0b',
    'editorGutter.modifiedBackground': '#6366f1',
    'editorGutter.addedBackground': '#22c55e',
    'editorGutter.deletedBackground': '#ef4444',

    // Editor bracket match
    'editorBracketMatch.background': '#6366f140',
    'editorBracketMatch.border': '#6366f1',

    // Editor overview ruler
    'editorOverviewRuler.border': '#27272a',
    'editorOverviewRuler.findMatchForeground': '#fbbf24',
    'editorOverviewRuler.errorForeground': '#ef4444',
    'editorOverviewRuler.warningForeground': '#eab308',

    // Editor widget
    'editorWidget.background': '#111113',
    'editorWidget.border': '#27272a',
    'editorWidget.foreground': '#e4e4e7',

    // Editor suggest widget
    'editorSuggestWidget.background': '#111113',
    'editorSuggestWidget.border': '#27272a',
    'editorSuggestWidget.foreground': '#e4e4e7',
    'editorSuggestWidget.selectedBackground': '#1a1a1d',
    'editorSuggestWidget.highlightForeground': '#6366f1',

    // Editor hover widget
    'editorHoverWidget.background': '#111113',
    'editorHoverWidget.border': '#27272a',

    // Minimap
    'minimap.background': '#0a0a0b',
    'minimap.selectionHighlight': '#6366f180',

    // Scrollbar
    'scrollbar.shadow': '#00000050',
    'scrollbarSlider.background': '#27272a80',
    'scrollbarSlider.hoverBackground': '#3f3f4680',
    'scrollbarSlider.activeBackground': '#52525b80',

    // Peek view
    'peekView.border': '#6366f1',
    'peekViewEditor.background': '#111113',
    'peekViewEditorGutter.background': '#0a0a0b',
    'peekViewResult.background': '#111113',
    'peekViewTitle.background': '#1a1a1d',

    // Input
    'input.background': '#1a1a1d',
    'input.border': '#27272a',
    'input.foreground': '#e4e4e7',
    'input.placeholderForeground': '#71717a',
    'inputOption.activeBorder': '#6366f1',

    // Dropdown
    'dropdown.background': '#111113',
    'dropdown.border': '#27272a',
    'dropdown.foreground': '#e4e4e7',

    // List
    'list.activeSelectionBackground': '#1a1a1d',
    'list.activeSelectionForeground': '#e4e4e7',
    'list.hoverBackground': '#1a1a1d80',
    'list.focusBackground': '#1a1a1d',
    'list.highlightForeground': '#6366f1',
  },
};

export const vibeThemePlugin: EditorPlugin = {
  name: 'vibe-theme',
  description: 'Custom Vibe Designer dark theme',

  activate(monaco) {
    monaco.editor.defineTheme('vibe-dark', VIBE_THEME);
    monaco.editor.setTheme('vibe-dark');
  },

  deactivate() {
    // Theme persists, no cleanup needed
  },
};
